import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wallet, Leaf, ClipboardCheck, Wrench, LineChart, Sparkles, Building2, GraduationCap, Tractor, CheckCircle2 } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";

const HOW_STEPS = [
  {
    icon: ClipboardCheck,
    title: "01 — Feasibility Audit",
    body: "We conduct a full roof and consumption review. You get a clear, plain-English recommendation. No sales pressure, no obligation.",
  },
  {
    icon: Wrench,
    title: "02 — We Install at £0",
    body: "SEED funds, installs and owns the system. We use tier-1 panels and experienced commercial solar installers — you carry no asset risk and no capex impact.",
  },
  {
    icon: LineChart,
    title: "03 — A Fixed kWh Rate for 25 Years",
    body: "Pay a single, agreed kWh rate for two and a half decades — locked in at signing and decoupled from volatile wholesale markets. Maintenance and monitoring included.",
  },
];

const WHY_POINTS = [
  {
    icon: Wallet,
    label: "Financial Security",
    body: "Predictable energy costs let trustees plan budgets a decade out without exposure to volatile wholesale markets.",
  },
  {
    icon: ShieldCheck,
    label: "Contractual Protection",
    body: "Year 9 exit option and a transparent 25-year agreement — designed to be reviewed alongside your solicitor, with no jargon traps and the indexation formula published before you sign.",
  },
  {
    icon: Leaf,
    label: "Green Impact",
    body: "Reduce your building's carbon footprint by up to 70%. Tangible ESG credentials for funders and members.",
  },
  {
    icon: Sparkles,
    label: "Zero Disruption",
    body: "We schedule installations around fixtures, term times and services. Operations continue uninterrupted.",
  },
];

const SECTORS = [
  {
    icon: Building2,
    title: "Sports Clubs",
    angle: "Committee Relief",
    body: "Protect club finances for the next generation of members.",
    to: "/sectors/sports-clubs",
  },
  {
    icon: GraduationCap,
    title: "Schools & Academies",
    angle: "Budget Liberation",
    body: "Reclaim operational budget for what actually matters: teaching.",
    to: "/sectors/schools",
  },
  {
    icon: Tractor,
    title: "Farms",
    angle: "Never Switch Off",
    body: "Fix a major overhead below your current tariff — for a quarter of a century.",
    to: "/sectors/farms",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden navy-panel grain" data-testid="home-hero">
        <div className="container-seed pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Now installing across Southern England
                </div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-6 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-[64px] leading-[1.02]">
                  The last <span className="text-emerald-400">electricity bill</span> you'll ever negotiate.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  £0 upfront. A fixed kWh rate for 25 years — decoupled from volatile wholesale markets,
                  and locked in at signing. Full maintenance, monitoring and insurance included.
                  Built for treasurers, bursars and trustees who plan in decades.
                </p>
              </FadeUp>
              <FadeUp delay={3}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    data-testid="hero-book-assessment-btn"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-4 px-6 rounded-lg transition-colors duration-200 group"
                  >
                    Book Assessment
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
                  </Link>
                  <Link
                    to="/case-study"
                    data-testid="hero-view-case-study-btn"
                    className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/15 font-semibold py-4 px-6 rounded-lg transition-colors duration-200"
                  >
                    See Chichester RFC case study
                  </Link>
                </div>
              </FadeUp>
              <FadeUp delay={4}>
                <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                  {[
                    ["£0", "Upfront cost"],
                    ["25 yrs", "Fixed rate"],
                    ["100%", "Maintained"],
                  ].map(([stat, label]) => (
                    <div key={label}>
                      <div className="text-emerald-400 font-display font-bold text-2xl sm:text-3xl">{stat}</div>
                      <div className="text-slate-400 text-xs mt-1">{label}</div>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>

            <FadeUp delay={2} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-emerald-500/10 blur-3xl rounded-full" aria-hidden />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <img
                    src={IMAGES.heroCommunity}
                    alt="Modern building with rooftop solar panels"
                    className="w-full h-[460px] object-cover"
                    loading="eager"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-xl bg-white shadow-xl p-5 border border-slate-200 max-w-[260px]">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Year 9 exit</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 leading-snug">
                    From your 9th year, stop purchasing on three months' notice. Optionality, in writing.
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- TRUST BAR ---------- */}
      <section className="border-y border-slate-200 bg-white" data-testid="home-trust-bar">
        <div className="container-seed py-6 flex flex-col md:flex-row items-center gap-3 md:gap-8 justify-between text-xs sm:text-sm text-slate-600">
          <div className="font-semibold text-slate-900">Built for trustees, bursars &amp; treasurers</div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-500">
            <span>£0 upfront installation</span>
            <span>·</span>
            <span>A fixed kWh rate for 25 years</span>
            <span>·</span>
            <span>Year 9 exit option</span>
            <span>·</span>
            <span>Full maintenance included</span>
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="container-seed section-pad" data-testid="home-how-it-works">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="text-overline">How it works</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Three steps. One signature.<br />
              A quarter-century of certainty.
            </h2>
          </div>
          <p className="md:col-span-5 text-slate-600 leading-relaxed">
            Most committees overthink solar. We've made it boring on purpose — zero funding cost, predictable install, predictable pricing.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {HOW_STEPS.map((s, i) => (
            <FadeUp key={s.title} delay={i}>
              <div className="bg-white border border-slate-200 rounded-xl p-8 h-full hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-emerald-600" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-display font-bold text-slate-900 text-xl">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm">{s.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- WHY SEED ---------- */}
      <section className="bg-slate-100/60 border-y border-slate-200" data-testid="home-why-seed">
        <div className="container-seed section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="text-overline">Why SEED</div>
              <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Engineered for the people who plan in <span className="text-emerald-600">decades</span>.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
                Trustees and bursars carry duty of care across multiple generations. Our model is built around
                that exact responsibility.
              </p>
              <div className="mt-8 rounded-xl overflow-hidden border border-slate-200">
                <img
                  src={IMAGES.commercialRoof}
                  alt="Aerial view of large rooftop solar panel installation"
                  className="w-full h-64 lg:h-80 object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {WHY_POINTS.map((p, i) => (
                <FadeUp key={p.label} delay={i}>
                  <div className="bg-white border border-slate-200 rounded-xl p-7 h-full">
                    <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center">
                      <p.icon className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 font-display font-bold text-slate-900 text-lg">{p.label}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed text-sm">{p.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- SECTORS ---------- */}
      <section className="container-seed section-pad" data-testid="home-sectors">
        <div className="max-w-2xl">
          <div className="text-overline">Built for your sector</div>
          <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            One model. Three very different responsibilities.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {SECTORS.map((s, i) => (
            <FadeUp key={s.title} delay={i}>
              <Link
                to={s.to}
                data-testid={`sector-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group block bg-white border border-slate-200 rounded-xl p-8 h-full hover:border-emerald-300 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center">
                    <s.icon className="w-6 h-6 text-emerald-400" strokeWidth={2} />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="mt-6 text-overline text-emerald-600">{s.angle}</div>
                <h3 className="mt-2 font-display font-bold text-slate-900 text-2xl">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm">{s.body}</p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <CTASection testId="home-cta" />
    </div>
  );
}
